import { useEffect, useState } from 'react';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import { closeConsentPrompt, useConsent, useConsentPromptOpen } from '@/lib/consent';

const COOKIE_CONSENT_ACCEPTED_EVENT = 'cookie_consent_selected';
const COOKIE_CONSENT_REJECTED_EVENT = 'cookie_consent_rejected';

const getCookieConsentEventProperties = (selection: 'accept' | 'reject') => ({
  option_selected_id: selection,
});

type UseConsentSurfaceResult = {
  shouldShow: boolean;
  accept: () => void;
  reject: () => void;
  dismiss: () => void;
};

export const useConsentSurface = (): UseConsentSurfaceResult => {
  const { allowed, decide, hydrated, isGdprRegion, isUndecided } = useConsent();
  const { track } = useAnalytics();
  const promptOpen = useConsentPromptOpen();
  const [shouldTrackAcceptAfterConsent, setShouldTrackAcceptAfterConsent] = useState(false);

  useEffect(() => {
    if (!shouldTrackAcceptAfterConsent || !allowed) {
      return undefined;
    }

    track(COOKIE_CONSENT_ACCEPTED_EVENT, getCookieConsentEventProperties('accept'));
    setShouldTrackAcceptAfterConsent(false);
    return undefined;
  }, [allowed, shouldTrackAcceptAfterConsent, track]);

  // Reject (including an undecided dismissal) must reach anonymous GrowthBook before consent changes; accept waits until consent allows the post-consent analytics path.
  const accept = () => {
    setShouldTrackAcceptAfterConsent(true);
    decide('accepted');
    closeConsentPrompt();
  };

  const reject = () => {
    track(COOKIE_CONSENT_REJECTED_EVENT, getCookieConsentEventProperties('reject'));
    decide('rejected');
    closeConsentPrompt();
  };

  const dismiss = () => {
    if (isUndecided) {
      reject();
      return;
    }
    closeConsentPrompt();
  };

  return {
    shouldShow: hydrated && isGdprRegion && (isUndecided || promptOpen),
    accept,
    reject,
    dismiss,
  };
};
